import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError, setToken, getToken } from "./api";

const AuthContext = createContext(null);
const PREVIEW_KEY = "ieftinit_preview_user_id";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=anonymous, object=logged in
  const [loading, setLoading] = useState(true);
  const [previewUser, setPreviewUser] = useState(null); // for admin preview-as-user

  const fetchMe = useCallback(async () => {
    if (!getToken()) { setUser(false); setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setToken(null);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  // Restore preview-user from sessionStorage on mount (clears on tab close).
  useEffect(() => {
    try {
      const cached = sessionStorage.getItem(PREVIEW_KEY);
      if (cached) setPreviewUser(JSON.parse(cached));
    } catch {}
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setToken(null);
    setUser(false);
    setPreviewUser(null);
    try { sessionStorage.removeItem(PREVIEW_KEY); } catch {}
  };

  /**
   * Enter "preview as user" mode (admin-only).
   * This is a CLIENT-SIDE simulation: sidebar/dashboard render as the previewed
   * user would see them, BUT all API calls continue to go out with the real
   * admin's token. Use only for visual previews — never as a security barrier.
   */
  const enterPreview = (previewed) => {
    if (!user || user.role !== "admin") return;
    setPreviewUser(previewed);
    try { sessionStorage.setItem(PREVIEW_KEY, JSON.stringify(previewed)); } catch {}
  };
  const exitPreview = () => {
    setPreviewUser(null);
    try { sessionStorage.removeItem(PREVIEW_KEY); } catch {}
  };

  // Effective user (preview overrides for UI checks only; real auth still uses `user`).
  const effective = previewUser || (user && user !== false ? user : null);

  const hasPermission = useCallback((perm) => {
    if (!effective) return false;
    const perms = effective.permissions || [];
    return perms.includes(normalizePermission(perm));
  }, [effective]);

  const hasAnyPermission = useCallback((...perms) => {
    if (!effective) return false;
    const set = new Set(effective.permissions || []);
    return perms.some((p) => set.has(normalizePermission(p)));
  }, [effective]);

  const value = useMemo(() => ({
    user, loading, login, logout, refresh: fetchMe,
    hasPermission, hasAnyPermission,
    previewUser, isPreviewing: !!previewUser,
    enterPreview, exitPreview,
    effectiveUser: effective,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [user, loading, hasPermission, hasAnyPermission, previewUser, effective]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Legacy permission keys → new dotted keys (mirror of backend core/permissions.py LEGACY_ALIASES)
const LEGACY_ALIASES = {
  view_returns: "returns.view",
  create_returns: "returns.create",
  edit_returns: "returns.edit",
  delete_returns: "returns.delete",
  export_returns: "returns.export",
  manage_statuses: "returns.manage_settings",
  view_service: "service.view",
  edit_service: "service.edit",
  delete_service: "service.delete",
  manage_service_settings: "service.manage_settings",
  manage_users: "users.permissions",
  view_audit_logs: "audit.view",
  manage_modules: "users.permissions",
  manage_settings: "users.permissions",
};

function normalizePermission(p) {
  return LEGACY_ALIASES[p] || p;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside <AuthProvider>");
  return ctx;
}

/** Convenience helper hook. */
export function usePermission(perm) {
  const { hasPermission } = useAuth();
  return hasPermission(perm);
}
